/**
 * Block 12 PR-B Phase B3 Part C Step 13 — computeMaterialRows.
 *
 * Builds Section Б material rows для текущей анкеты из `AnketaState` + per-type
 * methodology hints. Использует B2 frozen formula dispatchers (`corpusQ`,
 * `hdfQ`, `areaFacade`, `edgeBand`, per-anketa countertopQ).
 *
 * **Path 1 dispatch (ADR-0020 §«Pre-architecture clarifications» + handoff §G):**
 *   Kitchen с `handleType === 'гола'` → append дополнительный row 'gola-profile'
 *   (qty = `golaCountKitchen`). Для Kitchen с `'с ручками' | 'от нажатия'` —
 *   gola row отсутствует (handle/hinge rows будут в Section В, Phase B4).
 *
 * **Per-anketa material lists (ADR-0020 §Q1):**
 *   - Kitchen [facades, corpus, edge-band, hdf, countertop?, gola-profile?]
 *   - Cabinet [facades, corpus, edge-band, hdf]
 *   - BathTabletop [facades, corpus, edge-band, countertop] (no hdf per OQ-7)
 *   - BathShelfAbove [facades, corpus, edge-band, hdf]
 *   - BathCabinet [facades, corpus, edge-band, hdf]
 *   - TvZone [facades, corpus, edge-band, hdf]
 *   - Balcony [corpus, edge-band, hdf] (no facades per OQ-3)
 *   - Other [] (свободный режим — Phase 9+ через FreeAnketaForm)
 *
 * **Draft state robustness:** B2 formulas throw на недостающие dimensions
 * (length=undefined, height < 600 в Кухне). Helper wrap'ит в try/catch и
 * возвращает qty=0 для incomplete params — UI рендерит row с placeholder,
 * пользователь заполняет params, qty пересчитывается.
 *
 * Reference: ADR-0020 §Q1 + §Q4, методика §2.1-§2.7 Б, B2 frozen dispatchers.
 */

import {
  type CalculatorMethodology,
  type CalculatorParams,
  corpusQ,
  countertopQBathTabletop,
  countertopQKitchen,
  edgeBand,
  areaFacade,
  areaFacadeTvZone,
  facadeCount,
  golaCountKitchen,
  hdfQ,
  hingeCount,
} from '@pomoshnik/shared';
import {
  countertopDeeplink,
  countertopKindRussianToEnglish,
  countertopUnitRu,
} from './countertopKindMap';
import { assertNever, type AnketaState, type MaterialRow } from '../types';

/** Internal arg bag для buildRow — keeps caller sites concise. */
interface RowArgs {
  id: string;
  name: string;
  unit: string;
  qtyAuto: number;
  state: AnketaState;
  deeplink?: 'decor' | 'partner' | 'furniture' | 'handles';
}

function buildRow({ id, name, unit, qtyAuto, state, deeplink }: RowArgs): MaterialRow {
  const overrideValue = state.materialOverrides[id];
  const qtyOverridden = typeof overrideValue === 'number';
  const qty = qtyOverridden ? overrideValue : qtyAuto;
  const price = state.materialPrices[id] ?? 0;
  return {
    id,
    name,
    qty,
    qtyAuto,
    qtyOverridden,
    unit,
    price,
    cost: qty * price,
    deeplink,
  };
}

/**
 * Safe wrapper для B2 formula calls — formulas throw на incomplete params
 * (draft fill state, до того как все required fields filled — ADR-0020 §Q7).
 * Returns 0 для throws + non-finite/negative. В dev режиме console.warn,
 * чтобы реальные programming bugs (typo, NaN propagation) не молчали.
 */
function safeCompute(fn: () => number): number {
  try {
    const v = fn();
    if (Number.isFinite(v) && v >= 0) return v;
    if (import.meta.env.DEV) {
      console.warn('[computeMaterialRows] formula returned non-finite/negative:', v);
    }
    return 0;
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[computeMaterialRows] formula threw:', err);
    }
    return 0;
  }
}

export function computeMaterialRows(
  state: AnketaState,
  _methodology: CalculatorMethodology,
): MaterialRow[] {
  const params = state.params;
  // `state.params` — PartialOfVariant<CalculatorParams>. После discriminator
  // narrowing (switch) каждая ветка cast'ит к concrete variant: B2 formulas
  // type-check, runtime защищён `safeCompute` + `assertPositiveDimension`.
  switch (params.type) {
    case 'kitchen':
      return kitchenRows(state);
    case 'cabinet':
      return cabinetRows(state);
    case 'bath-tabletop':
      return bathTabletopRows(state);
    case 'bath-shelf-above':
      return bathShelfAboveRows(state);
    case 'bath-cabinet':
      return bathCabinetRows(state);
    case 'tv-zone':
      return tvZoneRows(state);
    case 'balcony':
      return balconyRows(state);
    case 'other':
      return [];
    default:
      return assertNever(params, 'computeMaterialRows');
  }
}

// ── Per-anketa row builders ─────────────────────────────────────────────────

/**
 * Phase B4 Step 5 — UX visible breakdown для Kitchen facades row. Каждый
 * включённый adjustment (penal / built-in fridge / shelf above) добавляет к
 * `areaFacade` фиксированный value (см. `packages/shared/src/calculator/formulas/areaFacade.ts`
 * KITCHEN_ADJUSTMENT_*). Возвращаем human-readable comma-joined string или
 * undefined если adjustments не активны (UI пропускает rendering).
 */
function kitchenFacadeAdjustments(
  params: CalculatorParams & { type: 'kitchen' },
): string | undefined {
  const parts: string[] = [];
  if (params.hasPenal) parts.push('+3.5 м² пенал');
  if (params.fridgeType === 'built-in') parts.push('+3.5 м² встроенный холодильник');
  if (params.hasShelfAboveFridge) parts.push('+1 м² шкаф над холодильником');
  return parts.length > 0 ? parts.join(', ') : undefined;
}

function kitchenRows(state: AnketaState): MaterialRow[] {
  const params = state.params as CalculatorParams & { type: 'kitchen' };
  const corpusQty = safeCompute(() => corpusQ(params));
  const facadeRow = buildRow({
    id: 'facades',
    name: 'Фасады',
    unit: 'м²',
    qtyAuto: safeCompute(() => areaFacade(params)),
    state,
    deeplink: 'partner',
  });
  const adjustmentsDetail = kitchenFacadeAdjustments(params);
  if (adjustmentsDetail) facadeRow.adjustmentsDetail = adjustmentsDetail;
  const rows: MaterialRow[] = [
    facadeRow,
    buildRow({
      id: 'corpus',
      name: 'Корпус ЛДСП',
      unit: 'листов',
      qtyAuto: corpusQty,
      state,
      deeplink: 'decor',
    }),
    buildRow({
      id: 'edge-band',
      name: 'Кромка',
      unit: 'м',
      qtyAuto: safeCompute(() => edgeBand(corpusQty)),
      state,
      deeplink: 'decor',
    }),
    buildRow({
      id: 'hdf',
      name: 'ХДФ (задняя стенка)',
      unit: 'листов',
      qtyAuto: safeCompute(() => hdfQ(params)),
      state,
      deeplink: 'decor',
    }),
  ];

  // Countertop row — kitchen всегда имеет countertopKind (Zod schema required).
  // Во время draft `params.countertopKind` может быть undefined — пропускаем row.
  if (params.countertopKind) {
    const kindEn = countertopKindRussianToEnglish(params.countertopKind);
    rows.push(
      buildRow({
        id: 'countertop',
        name: `Столешница (${params.countertopKind})`,
        unit: countertopUnitRu(kindEn),
        qtyAuto: safeCompute(() =>
          countertopQKitchen(params, {
            kind: kindEn,
            lvlsSheetLength: params.countertopSheetLen,
          }),
        ),
        state,
        deeplink: countertopDeeplink(kindEn),
      }),
    );
  }

  // Path 1 dispatch — handleType='гола' добавляет gola-profile row.
  // Per ADR-0020 + handoff §G: dispatch в UI слое, formulas frozen.
  if (params.handleType === 'гола') {
    rows.push(
      buildRow({
        id: 'gola-profile',
        name: 'Гола профиль',
        unit: 'шт',
        qtyAuto: safeCompute(() => golaCountKitchen(params)),
        state,
        deeplink: 'furniture',
      }),
    );
  }

  // Bug #6.1 — Section Б Hardware rows per методика §2.1 Б. Для handleType='гола'
  // ручек как отдельной позиции нет (профиль гола является fix-mounted handle и
  // уже учтён в gola-profile row выше) → handles row пропускается.
  if (params.handleType !== 'гола') {
    rows.push(
      buildRow({
        id: 'handles',
        name: `Ручки (${params.handleType})`,
        unit: 'шт',
        qtyAuto: safeCompute(() => facadeCount(params)),
        state,
        deeplink: 'handles',
      }),
    );
  }
  rows.push(
    buildRow({
      id: 'hinges',
      name: 'Петли',
      unit: 'шт',
      qtyAuto: safeCompute(() => hingeCount(params)),
      state,
      deeplink: 'furniture',
    }),
  );
  // qtyAuto=0 — количество ящиков задаёт user через override input (materialOverrides).
  // Per методика §2.1 Б: «Ящики выдвижные» — пользовательская позиция, нет формулы.
  rows.push(
    buildRow({
      id: 'drawer-mechanisms',
      name: 'Механизмы ящиков',
      unit: 'шт',
      qtyAuto: 0,
      state,
      deeplink: 'furniture',
    }),
  );

  return rows;
}

function cabinetRows(state: AnketaState): MaterialRow[] {
  const params = state.params as CalculatorParams & { type: 'cabinet' };
  const corpusQty = safeCompute(() => corpusQ(params));
  return [
    buildRow({
      id: 'facades',
      name: 'Фасады',
      unit: 'м²',
      qtyAuto: safeCompute(() => areaFacade(params)),
      state,
      deeplink: 'partner',
    }),
    buildRow({
      id: 'corpus',
      name: 'Корпус ЛДСП',
      unit: 'листов',
      qtyAuto: corpusQty,
      state,
      deeplink: 'decor',
    }),
    buildRow({
      id: 'edge-band',
      name: 'Кромка',
      unit: 'м',
      qtyAuto: safeCompute(() => edgeBand(corpusQty)),
      state,
      deeplink: 'decor',
    }),
    buildRow({
      id: 'hdf',
      name: 'ХДФ (задняя стенка)',
      unit: 'листов',
      qtyAuto: safeCompute(() => hdfQ(params)),
      state,
      deeplink: 'decor',
    }),
    // Bug #6.1 — Section Б Hardware rows per методика §2.2 Б.
    // Cabinet handleType ∈ {с ручками, от нажатия} (нет гола per CABINET_HANDLE_TYPES),
    // поэтому handles row всегда применима.
    buildRow({
      id: 'handles',
      name: `Ручки (${params.handleType})`,
      unit: 'шт',
      qtyAuto: safeCompute(() => facadeCount(params)),
      state,
      deeplink: 'handles',
    }),
    buildRow({
      id: 'hinges',
      name: 'Петли',
      unit: 'шт',
      qtyAuto: safeCompute(() => hingeCount(params)),
      state,
      deeplink: 'furniture',
    }),
    buildRow({
      id: 'drawer-mechanisms',
      name: 'Механизмы ящиков',
      unit: 'шт',
      qtyAuto: 0,
      state,
      deeplink: 'furniture',
    }),
  ];
}

function bathTabletopRows(state: AnketaState): MaterialRow[] {
  const params = state.params as CalculatorParams & { type: 'bath-tabletop' };
  const corpusQty = safeCompute(() => corpusQ(params));
  const rows: MaterialRow[] = [
    buildRow({
      id: 'facades',
      name: 'Фасады',
      unit: 'м²',
      qtyAuto: safeCompute(() => areaFacade(params)),
      state,
      deeplink: 'partner',
    }),
    buildRow({
      id: 'corpus',
      name: 'Корпус ЛДСП',
      unit: 'листов',
      qtyAuto: corpusQty,
      state,
      deeplink: 'decor',
    }),
    buildRow({
      id: 'edge-band',
      name: 'Кромка',
      unit: 'м',
      qtyAuto: safeCompute(() => edgeBand(corpusQty)),
      state,
      deeplink: 'decor',
    }),
  ];

  if (params.countertopKind) {
    const kindEn = countertopKindRussianToEnglish(params.countertopKind);
    rows.push(
      buildRow({
        id: 'countertop',
        name: `Столешница (${params.countertopKind})`,
        unit: countertopUnitRu(kindEn),
        // PR-A1 (ADR-0033) — передаём lvlsSheetLength чтобы ЛДСП уважал выбор
        // пользователя (3/4 м). Без этого Bath-tabletop ЛДСП был dead-fixed
        // 3000 (формула игнорировала). HPL фикс 4000 (formula игнорирует opt).
        qtyAuto: safeCompute(() =>
          countertopQBathTabletop(params, {
            kind: kindEn,
            lvlsSheetLength: params.countertopSheetLen,
          }),
        ),
        state,
        deeplink: countertopDeeplink(kindEn),
      }),
    );
  }

  return rows;
}

function bathShelfAboveRows(state: AnketaState): MaterialRow[] {
  const params = state.params as CalculatorParams & { type: 'bath-shelf-above' };
  const corpusQty = safeCompute(() => corpusQ(params));
  return [
    buildRow({
      id: 'facades',
      name: 'Фасады',
      unit: 'м²',
      qtyAuto: safeCompute(() => areaFacade(params)),
      state,
      deeplink: 'partner',
    }),
    buildRow({
      id: 'corpus',
      name: 'Корпус ЛДСП',
      unit: 'листов',
      qtyAuto: corpusQty,
      state,
      deeplink: 'decor',
    }),
    buildRow({
      id: 'edge-band',
      name: 'Кромка',
      unit: 'м',
      qtyAuto: safeCompute(() => edgeBand(corpusQty)),
      state,
      deeplink: 'decor',
    }),
    buildRow({
      id: 'hdf',
      name: 'ХДФ (задняя стенка)',
      unit: 'листов',
      qtyAuto: safeCompute(() => hdfQ(params)),
      state,
      deeplink: 'decor',
    }),
  ];
}

function bathCabinetRows(state: AnketaState): MaterialRow[] {
  const params = state.params as CalculatorParams & { type: 'bath-cabinet' };
  const corpusQty = safeCompute(() => corpusQ(params));
  return [
    buildRow({
      id: 'facades',
      name: 'Фасады',
      unit: 'м²',
      qtyAuto: safeCompute(() => areaFacade(params)),
      state,
      deeplink: 'partner',
    }),
    buildRow({
      id: 'corpus',
      name: 'Корпус ЛДСП',
      unit: 'листов',
      qtyAuto: corpusQty,
      state,
      deeplink: 'decor',
    }),
    buildRow({
      id: 'edge-band',
      name: 'Кромка',
      unit: 'м',
      qtyAuto: safeCompute(() => edgeBand(corpusQty)),
      state,
      deeplink: 'decor',
    }),
    buildRow({
      id: 'hdf',
      name: 'ХДФ (задняя стенка)',
      unit: 'листов',
      qtyAuto: safeCompute(() => hdfQ(params)),
      state,
      deeplink: 'decor',
    }),
  ];
}

function tvZoneRows(state: AnketaState): MaterialRow[] {
  const params = state.params as CalculatorParams & { type: 'tv-zone' };
  const corpusQty = safeCompute(() => corpusQ(params));
  return [
    buildRow({
      id: 'facades',
      name: 'Фасады',
      unit: 'м²',
      // Explicit areaFacadeTvZone — top-level dispatcher принимает vitrineProfile=false
      // дефолтом, но TvZoneParams хранит фактический выбор пользователя.
      qtyAuto: safeCompute(() =>
        areaFacadeTvZone(params, { vitrineProfile: params.vitrineProfile ?? false }),
      ),
      state,
      deeplink: 'partner',
    }),
    buildRow({
      id: 'corpus',
      name: 'Корпус ЛДСП',
      unit: 'листов',
      qtyAuto: corpusQty,
      state,
      deeplink: 'decor',
    }),
    buildRow({
      id: 'edge-band',
      name: 'Кромка',
      unit: 'м',
      qtyAuto: safeCompute(() => edgeBand(corpusQty)),
      state,
      deeplink: 'decor',
    }),
    buildRow({
      id: 'hdf',
      name: 'ХДФ (задняя стенка)',
      unit: 'листов',
      qtyAuto: safeCompute(() => hdfQ(params)),
      state,
      deeplink: 'decor',
    }),
  ];
}

function balconyRows(state: AnketaState): MaterialRow[] {
  const params = state.params as CalculatorParams & { type: 'balcony' };
  const corpusQty = safeCompute(() => corpusQ(params));
  return [
    buildRow({
      id: 'corpus',
      name: 'Корпус ЛДСП',
      unit: 'листов',
      qtyAuto: corpusQty,
      state,
      deeplink: 'decor',
    }),
    buildRow({
      id: 'edge-band',
      name: 'Кромка',
      unit: 'м',
      qtyAuto: safeCompute(() => edgeBand(corpusQty)),
      state,
      deeplink: 'decor',
    }),
    buildRow({
      id: 'hdf',
      name: 'ХДФ (задняя стенка)',
      unit: 'листов',
      qtyAuto: safeCompute(() => hdfQ(params)),
      state,
      deeplink: 'decor',
    }),
    // Bug #6.1 — Section Б Hardware rows per методика §2.7 Б. Балкон has facadeCount
    // formula even though areaFacade throws (нет МДФ-фасадов per OQ-3 — door pieces
    // counted, m² of facades not applicable).
    buildRow({
      id: 'handles',
      name: `Ручки (${params.handleType})`,
      unit: 'шт',
      qtyAuto: safeCompute(() => facadeCount(params)),
      state,
      deeplink: 'handles',
    }),
    buildRow({
      id: 'hinges',
      name: 'Петли',
      unit: 'шт',
      qtyAuto: safeCompute(() => hingeCount(params)),
      state,
      deeplink: 'furniture',
    }),
    buildRow({
      id: 'drawer-mechanisms',
      name: 'Механизмы ящиков',
      unit: 'шт',
      qtyAuto: 0,
      state,
      deeplink: 'furniture',
    }),
  ];
}
